import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spin } from "antd";
import logo from "@iso/assets/images/logo.png";
import { alertDeviceService } from "@iso/services";
import SafefarmRespondWrapper from "./SafefarmRespond.styles";

// Public response page for the SafeFarm alarm SMS link. No login —
// the SMS recipient lands here, picks one of three buttons, done.
// Intentionally PII-free: no device name, no user, no GPS.
const STATUS_TERMINATE = "terminate";
const STATUS_RESPONDING = "responding";
const STATUS_NOT_AVAILABLE = "not-available";

const STATUS_LABEL = {
  [STATUS_TERMINATE]: "Alarm terminated",
  [STATUS_RESPONDING]: "Marked as responding",
  [STATUS_NOT_AVAILABLE]: "Marked as not available",
};

// View modes — drives which block the card renders.
const VIEW_LOADING = "loading";
const VIEW_READY = "ready";        // buttons shown
const VIEW_DONE = "done";          // submission acknowledged
const VIEW_RESOLVED = "resolved";  // alarm already terminated by someone
const VIEW_RATE_LIMIT = "rateLimit";
const VIEW_INVALID = "invalid";

export default function SafefarmRespond() {
  const { token } = useParams();
  const [view, setView] = useState(VIEW_LOADING);
  const [submittedStatus, setSubmittedStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setView(VIEW_INVALID);
      return undefined;
    }
    alertDeviceService
      .publicGetByToken(token)
      .then((res) => {
        if (cancelled) return;
        if (!res || res.code !== "0000") {
          setView(VIEW_INVALID);
          return;
        }
        if (res.is_resolved) {
          setView(VIEW_RESOLVED);
          return;
        }
        if (
          typeof res.submission_count === "number" &&
          typeof res.submission_limit === "number" &&
          res.submission_count >= res.submission_limit
        ) {
          setView(VIEW_RATE_LIMIT);
          return;
        }
        setView(VIEW_READY);
      })
      .catch(() => {
        if (!cancelled) setView(VIEW_INVALID);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = (status) => {
    if (submitting) return;
    setSubmitting(true);
    alertDeviceService
      .publicUpdateStatus({ token, status })
      .then((res) => {
        if (res && res.code === "0000") {
          setSubmittedStatus(status);
          setView(VIEW_DONE);
        } else if (res && /too many/i.test(res.message || "")) {
          setView(VIEW_RATE_LIMIT);
        } else {
          setView(VIEW_INVALID);
        }
      })
      .catch(() => setView(VIEW_INVALID))
      .finally(() => setSubmitting(false));
  };

  const renderBody = () => {
    if (view === VIEW_LOADING) {
      return (
        <div className="respondState">
          <Spin />
        </div>
      );
    }

    if (view === VIEW_READY) {
      return (
        <>
          <div className="respondTitle">AirAgri Safety Alarm</div>
          <div className="respondSubtitle">Please respond</div>
          <div className="respondActions">
            <Button
              className="btnResponding"
              loading={submitting}
              onClick={() => submit(STATUS_RESPONDING)}
            >
              Responding
            </Button>
            <Button
              className="btnNotAvailable"
              loading={submitting}
              onClick={() => submit(STATUS_NOT_AVAILABLE)}
            >
              Not Available
            </Button>
            <Button
              className="btnTerminate"
              loading={submitting}
              onClick={() => submit(STATUS_TERMINATE)}
            >
              Terminate Alarm
            </Button>
          </div>
        </>
      );
    }

    if (view === VIEW_DONE) {
      return (
        <div className="respondState">
          <h2>{STATUS_LABEL[submittedStatus] || "Response recorded"}</h2>
          <p>Thanks — your response has been recorded.</p>
        </div>
      );
    }

    if (view === VIEW_RESOLVED) {
      return (
        <div className="respondState">
          <h2>Alarm already resolved</h2>
          <p>Someone has terminated this alarm. No action needed.</p>
        </div>
      );
    }

    if (view === VIEW_RATE_LIMIT) {
      return (
        <div className="respondState">
          <h2>Link no longer accepting responses</h2>
          <p>Too many submissions on this link. If the alarm is still active, please respond from the AirAgri app.</p>
        </div>
      );
    }

    return (
      <div className="respondState">
        <h2>Link not valid</h2>
        <p>This response link is invalid or expired.</p>
      </div>
    );
  };

  return (
    <SafefarmRespondWrapper>
      <div className="respondCard">
        <img className="respondLogo" src={logo} alt="AirAgri" />
        {renderBody()}
        <div className="respondFooter">AirAgri</div>
      </div>
    </SafefarmRespondWrapper>
  );
}
